import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    Link
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Upload, CloudUpload } from '@mui/icons-material';
import { contentService } from '../../services/api';

const SkillsUploadDialog = ({ open, onClose, onSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv']
        },
        maxFiles: 1,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                setSelectedFile(acceptedFiles[0]);
                setUploadResult(null);
            }
        }
    });

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('csvFile', selectedFile);

            const response = await contentService.importContentSkills(formData);
            setUploadResult(response.data);
            
            if (response.data.success) {
                onSuccess();
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadResult({
                success: false,
                message: error.response?.data?.message || 'Upload failed',
                errors: error.response?.data?.errors || []
            });
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setUploadResult(null);
        onClose();
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await contentService.exportContentsCsv();
            
            // Create blob and download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
            link.download = `content_skills_template_${timestamp}.csv`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading template:', error);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Update Content Skills
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Update skills for existing content using a CSV file.                        
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Required columns: Content ID, Content Name, Skills<br/>
                        Use | to separate multiple skills.<br/>
                        Example<br/>1, "JavaScript Basics", "JavaScript|Programming|Web Development"
                    </Typography>
                </Box>

                {!uploadResult && (
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'grey.300',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                            mb: 2
                        }}
                    >
                        <input {...getInputProps()} />
                        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        {selectedFile ? (
                            <Typography variant="body1">
                                Selected: {selectedFile.name}
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    {isDragActive ? 'Drop the CSV file here' : 'Drag and drop a CSV file here'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    or click to select a file
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

                {uploading && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Uploading and processing...
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {uploadResult && (
                    <Box sx={{ mt: 2 }}>
                        <Alert 
                            severity={uploadResult.success ? 'success' : 'error'}
                            sx={{ mb: 2 }}
                        >
                            {uploadResult.message}
                        </Alert>

                        {uploadResult.summary && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Import Summary:
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary={`Total Rows Processed: ${uploadResult.summary.totalRows}`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary={`Successfully Updated: ${uploadResult.summary.updated}`}
                                            primaryTypographyProps={{ color: 'success.main' }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary={`Skipped: ${uploadResult.summary.skipped}`}
                                            primaryTypographyProps={{ color: 'warning.main' }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary={`Errors: ${uploadResult.summary.errors}`}
                                            primaryTypographyProps={{ color: 'error.main' }}
                                        />
                                    </ListItem>
                                </List>
                            </Box>
                        )}

                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Errors:
                                </Typography>
                                <List dense>
                                    {uploadResult.errors.map((error, index) => (
                                        <ListItem key={index}>
                                            <ListItemText 
                                                primary={error}
                                                primaryTypographyProps={{ 
                                                    variant: 'body2',
                                                    color: 'error.main'
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}
                <Box sx={{ mb: 2 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={handleDownloadTemplate}
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        Download current content list as template
                    </Link>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {uploadResult ? 'Close' : 'Cancel'}
                </Button>
                {!uploadResult && (
                    <Button
                        onClick={handleUpload}
                        variant="contained"
                        disabled={!selectedFile || uploading}
                        startIcon={<Upload />}
                    >
                        {uploading ? 'Uploading...' : 'Upload & Update Skills'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SkillsUploadDialog;